'use client'

import { useState, useEffect } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { Button } from "@/components/elements/Button"
import { formatPrice, formatImageURL, isCustomerAuthActive, getBuyXTriggerCount, getBuyXGetYRewardRule, getCheckoutPath } from "@/utils/util"
import { BadgePercent, BanknoteArrowUp, Check, Gift, Medal, PackageX, Plus, ShoppingBag, Truck } from "lucide-react"
import CartCard from "./CartCard"
import { navigateUrl } from "@/utils/client-request"
import Skeleton from "@/components/elements/Skeleton"
import { useCart, useCartRecommendations } from "@/lib/commerce/hooks"
import { toast } from "@/lib/toast"
import { useSession } from "next-auth/react"
import ProductCard from "../product/ProductCard"
import { Progress } from "@/components/ui/progress"
import CartEmpty from "@/assets/cart-empty.png"
import Image from "next/image"

export default function Cart({ vendor, wishlist }: { vendor: any, wishlist?: any }) {
  const pathname = usePathname()
  const { data: session } = useSession()
  const { cart, initialLoading, isValidating, canCheckout, validateCart, buyXGetYInfo, addRewardItem, availableOrderRewardItems, rewardLoading, freeShippingProgress, amount, sale, activeOrderDiscount, orderDiscountAmount, appliedCoupon, getRewardPrice, isRewardOutOfStock, getBuyXGetYRewardState, cartTotal, loyaltyDiscountAmount, loyaltySaleOffsetAmount } = useCart()
  const loyaltyDiscount = loyaltyDiscountAmount > 0 ? Math.min(loyaltyDiscountAmount, Math.max(0, cartTotal - orderDiscountAmount)) : 0
  const [hasMounted, setHasMounted] = useState(false)
  const searchParams = useSearchParams()

  const products = cart?.products || []
  const isLoggedIn = !!session?.user?.id
  const checkoutHref = navigateUrl(getCheckoutPath(vendor, isLoggedIn), pathname)
  const { recommendedProducts, isLoading: isLoadingRecommendations } = useCartRecommendations(vendor, products, 5)

  useEffect(() => { setHasMounted(true) }, [])

  useEffect(() => {
    if (searchParams.has('error')) toast.error('Бүртгэл олдсонгүй')
  }, [searchParams])

  useEffect(() => {
    if (products.length > 0 && vendor) validateCart()
  }, [products.length, vendor])

  if (!hasMounted) return null

  return (
    <div className="cont py-8 sm:py-10">
      {initialLoading ? (
        <div className="lg:grid lg:grid-cols-12 lg:gap-x-8 space-y-8 lg:space-y-0">
          <div className="lg:col-span-8">
            <Skeleton className="w-full h-8" />
            <Skeleton className="mt-10 w-full h-44" />
            <Skeleton className="mt-10 w-full h-44" />
          </div>
          <div className="lg:col-span-4">
            <Skeleton className="w-full h-8" />
            <Skeleton className="mt-10 w-full h-32" />
            <Skeleton className="my-4 w-full h-16" />
            <Skeleton className="pt-4 w-full h-24" />
          </div>
        </div>
      ) : (
        <>
          {products?.length > 0 ? (
            <>
              <div className="lg:grid lg:grid-cols-12 lg:gap-x-8 space-y-8 lg:space-y-0">
                <div className="lg:col-span-7">
                  <div className="flex items-center text-xl font-semibold leading-7 tracking-[.0125em] text-foreground">Миний сагс</div>
                  <div className="mt-5 sm:mt-7">
                    <div className="flex flex-col space-y-5">
                      {products?.map((product: any, index: number) => (
                        <CartCard key={`${product.isRewardItem ? 'reward-' : ''}${product.id}-${product.variant?.id}-${index}`} product={product} />
                      ))}
                    </div>
                  </div>
                  {buyXGetYInfo?.map((info: any) => info.qualifies && info.rewardProducts?.length > 0 && (() => {
                    const { totalRemaining } = getBuyXGetYRewardState(info, info.rewardProducts[0])
                    if (totalRemaining <= 0) return null
                    return (
                      <div key={info.discount.id} className="mt-6 rounded-xl bg-linear-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 border-2 border-orange-500/30 border-dashed p-4">
                        <div className="flex items-center gap-2">
                          <div className="px-2 py-1 bg-orange-600 text-white rounded-full shrink-0">
                            <Gift className="w-5 h-5" strokeWidth={2.5} />
                          </div>
                          <div>
                            <div className="font-semibold text-sm text-foreground leading-5">{info.discount?.name || 'Хөнгөлөлт'}</div>
                            <div className="text-xs text-muted-foreground">{getBuyXTriggerCount(info.discount)} авбал {getBuyXGetYRewardRule(info)}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 mt-2">
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-xs text-green-600 font-medium">Нөхцөл хангагдлаа ({info.triggerCount}/{getBuyXTriggerCount(info.discount)}){totalRemaining > 0 ? ` — ${totalRemaining} урамшуулалтай бараа авах боломжтой` : ''}</span>
                        </div>
                        <div className="space-y-2 mt-3">
                          {info.rewardProducts.map((reward: any) => {
                            const { canAdd, isLockedByOther, isOutOfStock, rewardPrice } = getBuyXGetYRewardState(info, reward)
                            if (isLockedByOther) return null
                            return (
                              <div key={reward.id} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                                <div className="w-14 h-14 shrink-0">
                                  <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full rounded-md object-cover" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <div className="font-medium text-sm text-foreground leading-5">{reward.name}</div>
                                    {reward.valueType === 'free' ? (
                                      <span className="bg-red-500 text-white px-1.5 py-0.5 rounded text-[10px] font-semibold leading-3">Үнэгүй</span>
                                    ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                      <span className="bg-red-500 text-white px-1.5 py-0.5 rounded text-[10px] font-semibold leading-3">-{reward.valuePercent}%</span>
                                    ) : null}
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className={`text-sm font-medium ${rewardPrice === 0 ? 'text-green-600' : 'text-foreground'}`}>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
                                    {reward.price && rewardPrice < reward.price && <span className="line-through text-muted-foreground text-xs">{formatPrice(reward.price)}</span>}
                                  </div>
                                </div>
                                <Button onClick={() => addRewardItem({ ...reward, discountId: info.discount.id, discountName: info.discount.name, discountBase: info.discount.discountBase, variant: reward.variant || { id: reward.defaultVariantId, price: reward.price, priceSale: reward.priceSale, stock: reward.stock } }, 'buyXgetY')} size="sm" disabled={!canAdd || rewardLoading} isLoading={rewardLoading} loadingMessage="Нэмэх" color={vendor?.color?.primary} icon={canAdd && !rewardLoading ? Plus : undefined}>
                                  {isOutOfStock ? 'Дууссан' : canAdd ? 'Нэмэх' : 'Нэмэгдсэн'}
                                </Button>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })())}
                  {availableOrderRewardItems?.length > 0 && (
                    <div className="mt-6 rounded-xl bg-linear-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 border-2 border-orange-500/30 border-dashed p-4">
                      <div className="flex items-center gap-2">
                        <div className="px-2 py-1 bg-orange-600 text-white rounded-full shrink-0">
                          <Gift className="w-5 h-5" strokeWidth={2.5} />
                        </div>
                        <div>
                          <div className="font-semibold text-sm text-foreground leading-5">Захиалгын бэлэг</div>
                          <div className="text-xs text-muted-foreground">Захиалгын хөнгөлөлтийн бэлэг</div>
                        </div>
                      </div>
                      <div className="space-y-2 mt-3">
                        {availableOrderRewardItems.map((reward: any) => {
                          const isOutOfStock = isRewardOutOfStock(reward)
                          const rewardPrice = getRewardPrice(reward)
                          return (
                            <div key={reward.id} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                              <div className="w-14 h-14 shrink-0">
                                <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full rounded-md object-cover" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <div className="font-medium text-sm text-foreground leading-5">{reward.name}</div>
                                  {reward.valueType === 'free' ? (
                                    <span className="bg-red-500 text-white px-1.5 py-0.5 rounded text-[10px] font-semibold leading-3">Үнэгүй</span>
                                  ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                    <span className="bg-red-500 text-white px-1.5 py-0.5 rounded text-[10px] font-semibold leading-3">-{reward.valuePercent}%</span>
                                  ) : null}
                                </div>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className={`text-sm font-medium ${rewardPrice === 0 ? 'text-green-600' : 'text-foreground'}`}>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
                                  {reward.price && rewardPrice < reward.price && <span className="line-through text-muted-foreground text-xs">{formatPrice(reward.price)}</span>}
                                </div>
                              </div>
                              <Button onClick={() => addRewardItem(reward, 'order')} size="sm" disabled={isOutOfStock || rewardLoading} isLoading={rewardLoading} loadingMessage="Нэмэх" color={vendor?.color?.primary} icon={!isOutOfStock && !rewardLoading ? Plus : undefined}>{isOutOfStock ? 'Дууссан' : 'Нэмэх'}</Button>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="lg:col-span-1">
                  <div className="hidden lg:block h-full border-r border-border mt-14 max-h-[440px]"></div>
                </div>
                {products?.length > 0 && (
                  <div className="lg:col-span-4 mt-6 sm:mt-14">
                    <div className="flex flex-col items-center space-y-5">
                      {freeShippingProgress && !products.every((p: any) => p.isDigital === true) && (
                        <div className="flex flex-col gap-2 p-3 bg-green-50 rounded-lg w-full">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-1.5 text-green-700">
                              <Truck className="w-4 h-4" />
                              <span className="font-medium">Үнэгүй хүргэлт</span>
                            </div>
                            {freeShippingProgress.reached ? (
                              <div className="flex items-center gap-1 text-green-700 text-xs">
                                <Check className="w-3.5 h-3.5" />
                                <span className="font-medium">Хангагдсан</span>
                              </div>
                            ) : (
                              <span className="text-green-700 text-xs">{formatPrice(freeShippingProgress.remaining)} дутуу</span>
                            )}
                          </div>
                          <Progress value={freeShippingProgress.percentage} className="h-2 bg-green-200 [&>div]:bg-green-600" />
                        </div>
                      )}
                      {activeOrderDiscount?.type === 'order' && !appliedCoupon && activeOrderDiscount.minOrderAmount > 0 && (() => {
                        const subPrice = amount - sale
                        const minAmt = activeOrderDiscount.minOrderAmount
                        const target = activeOrderDiscount.targets?.find((t: any) => t.role === 'target' && t.targetId === null)
                        if (!target) return null
                        const discountLabel = target.valueType === 'percent' ? `${target.valuePercent}%-ийн хөнгөлөлт` : target.valueType === 'amount' ? `${formatPrice(target.valueAmount)} хөнгөлөлт` : null
                        if (!discountLabel) return null
                        const reached = subPrice >= minAmt
                        return (
                          <div className="flex flex-col gap-2.5 p-3 bg-purple-50 rounded-lg w-full">
                            <div className="flex items-start gap-1.5 text-purple-700 text-sm">
                              <BadgePercent className="w-4 h-4 shrink-0 mt-0.5" />
                              <span className="font-medium">{formatPrice(minAmt)}-с дээш захиалгад {discountLabel}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <Progress value={Math.min((subPrice / minAmt) * 100, 100)} className="h-2 flex-1 bg-purple-200 [&>div]:bg-purple-600" />
                              {reached ? (
                                <div className="flex items-center gap-1 text-purple-700 text-xs shrink-0">
                                  <Check className="w-3.5 h-3.5" />
                                  <span className="font-medium">-{formatPrice(orderDiscountAmount)}</span>
                                </div>
                              ) : (
                                <span className="text-purple-700 text-xs font-medium shrink-0">{formatPrice(minAmt - subPrice)} дутуу</span>
                              )}
                            </div>
                          </div>
                        )
                      })()}
                      {loyaltyDiscount > 0 && (
                        <div className="flex items-center justify-between w-full text-sm text-violet-600">
                          <div className="flex items-center gap-1.5">
                            <Medal className="w-4 h-4" />
                            <span className="font-medium">Лоялти хөнгөлөлт</span>
                          </div>
                          <div className="font-medium tabular-nums">-{formatPrice(loyaltyDiscount + loyaltySaleOffsetAmount)}</div>
                        </div>
                      )}
                      <div className="flex items-center justify-between w-full font-medium text-xl leading-7 tracking-[.0125em] text-foreground">
                        <div>Үнийн дүн</div>
                        <div>{formatPrice(Math.max(0, cartTotal - orderDiscountAmount - loyaltyDiscount))}</div>
                      </div>
                      <div className="flex flex-col gap-2 w-full items-center justify-center">
                        <Button onClick={() => { window.location.href = checkoutHref }} border={vendor?.border} className="w-full" color={vendor?.color?.primary} disabled={isValidating || !canCheckout()}>
                          Төлбөр төлөх
                        </Button>
                        <div className="font-normal text-xs text-muted-foreground leading-5">
                          Төлбөр төлөх үед хүргэлтийн төлбөр тооцогдоно.
                        </div>
                        {vendor?.orderMinAmount > 0 && (amount - sale) < vendor.orderMinAmount && (
                          <div className="flex flex-col gap-2 p-3 bg-blue-50 rounded-lg w-full">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-1.5 text-blue-700">
                                <ShoppingBag className="w-4 h-4" />
                                <span className="font-medium">Захиалгын доод дүн</span>
                              </div>
                              <span className="text-blue-700 text-xs">{formatPrice(vendor.orderMinAmount - (amount - sale))} дутуу</span>
                            </div>
                            <Progress value={((amount - sale) / vendor.orderMinAmount) * 100} className="h-2 bg-blue-200 [&>div]:bg-blue-600" />
                          </div>
                        )}
                      </div>
                      {cart?.products?.some((product: any) => product.quantity === 0 || product.isOutOfStock) && (
                        <div className="p-4 bg-red-50 border border-red-100 rounded-lg">
                          <div className="flex items-start gap-3">
                            <PackageX className='w-5 h-5 text-red-500 shrink-0 mt-1' />
                            <div className="flex flex-col gap-1">
                              <span className="text-sm text-red-500 font-medium leading-6">Үлдэгдэл дууссан бараа</span>
                              <span className='text-sm text-red-500 font-normal leading-5'>
                                Таны сагсанд үлдэгдэл хүрэлцэхгүй бараа байна. Эдгээр барааг устгаад захиалга өгнө үү.
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      {vendor?.terms?.delivery && (
                        <div className="border border-border p-4 rounded-lg w-full">
                          <div className="flex items-start gap-3">
                            <Truck className="w-5 h-5 mt-1 shrink-0" />
                            <div className="flex flex-col gap-1">
                              <div className="font-medium text-foreground leading-6 text-sm">Хүргэлтийн нөхцөл</div>
                              <div className="prose prose-sm max-w-none text-foreground leading-5 text-sm font-normal" dangerouslySetInnerHTML={{ __html: vendor.terms.delivery }} />
                            </div>
                          </div>
                        </div>
                      )}
                      {vendor?.terms?.payment && (
                        <div className="border border-border p-4 rounded-lg w-full">
                          <div className="flex items-start gap-3">
                            <BanknoteArrowUp className="w-5 h-5 mt-1 shrink-0" />
                            <div className="flex flex-col gap-1">
                              <div className="font-medium text-foreground leading-6 text-sm">Төлбөрийн нөхцөл</div>
                              <div className="prose prose-sm max-w-none text-foreground leading-5 text-sm font-normal" dangerouslySetInnerHTML={{ __html: vendor.terms.payment }} />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {isCustomerAuthActive(vendor) && isLoggedIn ? (
                <div className="mt-5">
                  {wishlist && wishlist.products && wishlist.products.length > 0 ? (
                    <>
                      <div className="font-medium text-xl leading-7 tracking-[.0125em] text-foreground">
                        Хүслийн жагсаалт
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-8 mt-5">
                        {wishlist.products.map((product: any, index: number) => {
                          const uniqueKey = `${product.id}-${product.variant?.id || `wishlist-${index}`}`
                          return <ProductCard key={uniqueKey} product={product} vendor={vendor} showVariantName />
                        })}
                      </div>
                    </>
                  ) : (
                    <>
                      {isLoadingRecommendations ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-8 mt-5">
                          {[...Array(5)].map((_, i) => <Skeleton key={i} className="w-full h-64" />)}
                        </div>
                      ) : recommendedProducts.length > 0 ? (
                        <>
                          <div className="font-medium text-xl leading-7 tracking-[.0125em] text-foreground">Санал болгох бүтээгдэхүүн</div>
                          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-8 mt-5">
                            {recommendedProducts.map((product: any) => (
                              <ProductCard key={product.customId} product={product} vendor={vendor} />
                            ))}
                          </div>
                        </>
                      ) : null}
                    </>
                  )}
                </div>
              ) : (
                (isLoadingRecommendations || recommendedProducts.length > 0) && (
                  <div className="mt-12 sm:mt-16">
                    <div className="font-medium text-xl leading-7 tracking-[.0125em] text-foreground">Санал болгох бүтээгдэхүүн</div>
                    <div className="mt-4">
                      {isLoadingRecommendations ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-8 mt-5">
                          {[...Array(5)].map((_, i) => <Skeleton key={i} className="w-full h-64" />)}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-8 mt-5">
                          {recommendedProducts.map((product: any) => (
                            <ProductCard key={product.customId} product={product} vendor={vendor} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              )}
            </>
          ) : (
            <div className="cont flex flex-col items-center justify-center">
              <Image src={CartEmpty} alt="Cart Empty" className="w-72 h-72 mb-4" />
              <div className="text-foreground text-center text-lg sm:text-xl leading-8 tracking-[.0125em] font-gilroy font-semibold">Таны сагс хоосон байна</div>
              <div className="mt-1 font-normal text-sm text-muted-foreground text-center">Та яг одоо сагсандаа бараа нэмээрэй!</div>
              <Button onClick={() => { window.location.href = navigateUrl('/product', pathname) }} className="mt-6" color={vendor?.color?.primary} border={vendor?.border} icon={ShoppingBag}>
                Дэлгүүр хэсэх
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
